import * as THREE from 'three';
import { createGlowTexture } from '../utils/createGlowTexture.js';

const SKY_RADIUS = 800;

// Icy blue-white glow for the head
const SHOOTING_STAR_GLOW = createGlowTexture('200, 232, 255');

const TRAIL_LENGTH = 30;
const FLIGHT_DURATION = 6.5; // seconds for one full sweep - fast
const RESPAWN_MIN = 25;
const RESPAWN_MAX = 45;

/**
 * ShootingStar - a moving streak across the sky.
 *
 * Used by the Wishing Stars system: each shooting star carries one wish
 * from one of Megha's people. Click the head while it's flying to catch
 * it (opens the wish card).
 *
 * Visual: thin, fast icy-blue head with a short fading particle trail.
 *
 * Structure (flat - no Group wrapping - so raycasting against the head
 * sprite works the same way it does for Sirius):
 *  - this.mesh    = head Sprite (top-level, also the click target)
 *  - this.trail   = separate THREE.Points object added to the sky
 *                   alongside the head, updated by this class
 */
export class ShootingStar {
  constructor({ onClick, isCardOpen, cardHTML, discoveryId = 'shooting-star' }) {
    this.id = discoveryId;
    this.discoveryId = discoveryId;
    this.category = 'continuous';
    this.interactable = true;
    this.onClickCallback = onClick;
    this.isCardOpen = isCardOpen || (() => false);
    this._cardHTML = cardHTML || '';
    this._hovering = false;

    // === Head: top-level sprite, also the click target ===
    const headMaterial = new THREE.SpriteMaterial({
      map: SHOOTING_STAR_GLOW,
      color: 0xc8e8ff,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false,
    });
    this.mesh = new THREE.Sprite(headMaterial);
    this.mesh.scale.setScalar(24); // thinner than before
    this.mesh.renderOrder = 11;
    this.mesh.userData.star = this;

    // === Trail: separate Points object ===
    this.trailHistory = []; // newest first
    const positions = new Float32Array(TRAIL_LENGTH * 3);
    const colors = new Float32Array(TRAIL_LENGTH * 3);

    for (let i = 0; i < TRAIL_LENGTH; i++) {
      positions[i * 3] = 0;
      positions[i * 3 + 1] = 0;
      positions[i * 3 + 2] = 0;
      const fade = 1 - i / TRAIL_LENGTH;
      const brightness = Math.pow(fade, 2.0) * 0.4;
      colors[i * 3] = 0.78 * brightness;
      colors[i * 3 + 1] = 0.91 * brightness;
      colors[i * 3 + 2] = 1.0 * brightness;
    }

    this.trailGeometry = new THREE.BufferGeometry();
    this.trailGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.trailGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const trailMaterial = new THREE.PointsMaterial({
      vertexColors: true,
      size: 1.6, // thinner trail
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.55,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    this.trail = new THREE.Points(this.trailGeometry, trailMaterial);
    this.trail.renderOrder = 10;

    // === State ===
    this.state = 'flying'; // 'flying' | 'waiting'
    this.t = 0;
    this.waitTimer = 0;
    this.respawnDelay = RESPAWN_MIN + Math.random() * (RESPAWN_MAX - RESPAWN_MIN);
    this.spawnNewPath();
  }

  spawnNewPath() {
    const startAz = Math.random() * 360;
    const startAlt = 25 + Math.random() * 50;
    const sweep = 80 + Math.random() * 70;
    const endAz = (startAz + sweep) % 360;
    const endAlt = 25 + Math.random() * 50;
    this.startPos = azAlt(startAz, startAlt);
    this.endPos = azAlt(endAz, endAlt);
    this.t = 0;
    this.trailHistory = [];
    this.mesh.visible = true;
    this.trail.visible = true;
    this.mesh.position.copy(this.startPos);
  }

  /**
   * Mark this star as caught - immediately moves to 'waiting' state and
   * hides the visuals. The owning system will detect the state change and
   * remove the object from the sky.
   */
  caught() {
    this.state = 'waiting';
    this.waitTimer = 0;
    this.mesh.visible = false;
    this.trail.visible = false;
    this.trailHistory = [];
    const positions = this.trailGeometry.attributes.position.array;
    for (let i = 0; i < TRAIL_LENGTH; i++) {
      positions[i * 3] = 0;
      positions[i * 3 + 1] = 0;
      positions[i * 3 + 2] = 0;
    }
    this.trailGeometry.attributes.position.needsUpdate = true;
  }

  onHover() {
    document.body.classList.add('star-hover');
    this._hovering = true;
  }

  onUnhover() {
    document.body.classList.remove('star-hover');
    this._hovering = false;
  }

  onClick() {
    if (this.onClickCallback) this.onClickCallback(this);
    this.caught();
  }

  cardHTML() {
    return this._cardHTML;
  }

  update(dt) {
    if (this.isCardOpen()) return;

    if (this.state === 'waiting') {
      // Wishing Stars System owns this - it'll detect 'waiting' and remove us.
      return;
    }

    this.t += dt;
    if (this.t > FLIGHT_DURATION) {
      this.state = 'waiting';
      this.mesh.visible = false;
      this.trail.visible = false;
      this.trailHistory = [];
      const positions = this.trailGeometry.attributes.position.array;
      for (let i = 0; i < TRAIL_LENGTH; i++) {
        positions[i * 3] = 0;
        positions[i * 3 + 1] = 0;
        positions[i * 3 + 2] = 0;
      }
      this.trailGeometry.attributes.position.needsUpdate = true;
      return;
    }

    const t = this.t / FLIGHT_DURATION;
    const eased = easeInOutCubic(t);
    const pos = lerpOnSphere(this.startPos, this.endPos, eased);
    this.mesh.position.copy(pos);

    this.trailHistory.unshift(pos.clone());
    if (this.trailHistory.length > TRAIL_LENGTH) this.trailHistory.pop();

    const positions = this.trailGeometry.attributes.position.array;
    for (let i = 0; i < TRAIL_LENGTH; i++) {
      const p = this.trailHistory[i] || pos;
      positions[i * 3] = p.x;
      positions[i * 3 + 1] = p.y;
      positions[i * 3 + 2] = p.z;
    }
    this.trailGeometry.attributes.position.needsUpdate = true;
  }
}

function azAlt(azDeg, altDeg) {
  const az = (azDeg * Math.PI) / 180;
  const alt = (altDeg * Math.PI) / 180;
  const cosAlt = Math.cos(alt);
  return new THREE.Vector3(
    SKY_RADIUS * cosAlt * Math.sin(az),
    SKY_RADIUS * Math.sin(alt),
    -SKY_RADIUS * cosAlt * Math.cos(az),
  );
}

function lerpOnSphere(a, b, t) {
  const aN = a.clone().normalize();
  const bN = b.clone().normalize();
  const dot = Math.max(-1, Math.min(1, aN.dot(bN)));
  const angle = Math.acos(dot);
  const sinAngle = Math.sin(angle);
  if (sinAngle < 0.001) {
    return a.clone().lerp(b, t);
  }
  const wA = Math.sin((1 - t) * angle) / sinAngle;
  const wB = Math.sin(t * angle) / sinAngle;
  return new THREE.Vector3(
    aN.x * wA + bN.x * wB,
    aN.y * wA + bN.y * wB,
    aN.z * wA + bN.z * wB,
  ).multiplyScalar(SKY_RADIUS);
}

function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}
